// pages/secure/ChecksPage.js
// The check register for hand-written checks: record what was written, see which
// numbers are missing from the book, and mark checks cleared against a statement.
//
// The two ways a check meets the ledger are surfaced explicitly in the form,
// because getting it wrong double-counts money: a check that pays something the
// ledger has already recorded (payroll net pay) must NOT create a second entry.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from '../../components/secure/Modal';
import DataList from '../../components/shared/DataList';

const money = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US') : '');
const iso = (d) => (d ? String(d).slice(0, 10) : '');
const today = () => new Date().toISOString().slice(0, 10);

const STATUS_TONE = {
    outstanding: 'bg-amber-100 text-amber-800',
    cleared: 'bg-green-100 text-green-700',
    void: 'bg-slate-200 text-slate-600',
};

// The payroll liabilities a deposit check clears, plus the everyday expense codes.
const COMMON_ACCOUNTS = [
    ['2110', 'Federal Income Tax Payable — 941 deposit'],
    ['2120', 'IL Income Tax Payable — IL-501 deposit'],
    ['2130', 'FICA Payable — Employee'],
    ['2140', 'FICA Payable — Employer'],
    ['2150', 'FUTA Payable — 940'],
    ['2160', 'SUTA Payable — IDES'],
    ['2000', 'Accounts Payable'],
    ['6200', 'Office Rent'],
    ['6300', 'Advertising & Marketing'],
    ['6400', 'MLS & Association Dues'],
    ['6500', 'Software & Subscriptions'],
    ['6600', 'Insurance (E&O, liability)'],
    ['6700', 'Professional Fees'],
    ['6800', 'Office Supplies'],
    ['6990', 'Miscellaneous Expense'],
];

const ChecksPage = ({ user }) => {
    const api = useApiClient();
    const [tab, setTab] = useState('register');
    const [checks, setChecks] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [gaps, setGaps] = useState([]);
    const [outstanding, setOutstanding] = useState(null);
    const [form, setForm] = useState(null);
    const [bookForm, setBookForm] = useState(null);
    const [error, setError] = useState('');
    const [flash, setFlash] = useState('');
    const [busy, setBusy] = useState(false);

    const note = (m) => { setFlash(m); setTimeout(() => setFlash(''), 5000); };
    const fail = (e) => setError(e.message || String(e));

    const load = useCallback(() => {
        setError('');
        api.get('/api/checks/accounts').then(setAccounts).catch(fail);
        if (tab === 'register') api.get('/api/checks').then(setChecks).catch(fail);
        if (tab === 'gaps') api.get('/api/checks/gaps').then(setGaps).catch(fail);
        if (tab === 'outstanding') api.get('/api/checks/outstanding').then(setOutstanding).catch(fail);
    }, [api, tab]);

    useEffect(() => { load(); }, [load]);

    const openNew = () => setForm({
        bank_account_id: accounts[0]?.bank_account_id || '',
        check_number: accounts[0]?.next_check_number || '',
        check_date: today(), payee: '', amount: '', memo: '', account_code: '',
    });

    const save = async () => {
        setBusy(true); setError('');
        try {
            if (form.check_id) {
                await api.put(`/api/checks/${form.check_id}`, form);
                note(`Check ${form.check_number} updated.`);
            } else {
                await api.post('/api/checks', form);
                note(`Check ${form.check_number} recorded.`);
            }
            setForm(null); load();
        } catch (e) { fail(e); } finally { setBusy(false); }
    };

    const voidCheck = async (c) => {
        const reason = window.prompt(`Void check ${c.check_number} to ${c.payee}?\n\nThe number stays used and is never reissued.\n\nReason:`);
        if (reason === null) return;
        try { const r = await api.post(`/api/checks/${c.check_id}/void`, { reason }); note(r.message); load(); }
        catch (e) { fail(e); }
    };

    const clearCheck = async (c) => {
        const d = window.prompt(`Date check ${c.check_number} cleared the bank:`, today());
        if (!d) return;
        try { await api.post(`/api/checks/${c.check_id}/clear`, { cleared_date: d }); note(`Check ${c.check_number} marked cleared.`); load(); }
        catch (e) { fail(e); }
    };

    const removeCheck = async (c) => {
        if (!window.confirm(`Remove check ${c.check_number} from the register?\n\nThe record is retained, and any ledger entry it created is voided.`)) return;
        try { const r = await api.delete(`/api/checks/${c.check_id}`); note(r.message); load(); }
        catch (e) { fail(e); }
    };

    const saveBook = async () => {
        setBusy(true);
        try {
            if (bookForm.bank_account_id) await api.put(`/api/checks/accounts/${bookForm.bank_account_id}`, bookForm);
            else await api.post('/api/checks/accounts', bookForm);
            setBookForm(null); note('Check book saved.'); load();
        } catch (e) { fail(e); } finally { setBusy(false); }
    };

    const btn = 'text-sm px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300';
    const ghost = 'text-sm px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50';
    const input = 'mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm';
    const label = 'block text-xs font-medium text-slate-600';

    return (
        <div className="p-6 max-w-6xl">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Check Register</h1>
                    <p className="text-slate-500 text-sm mt-0.5">
                        Checks are written by hand and recorded here. No check is printed, and no routing or account number is stored.
                    </p>
                </div>
                <button className={btn} onClick={openNew} disabled={!accounts.length}>+ Record a check</button>
            </div>

            <div className="flex gap-4 border-b border-slate-200 mt-4 mb-5">
                {[['register', 'Register'], ['gaps', 'Missing numbers'], ['outstanding', 'Outstanding'], ['books', 'Check books']].map(([id, lbl]) => (
                    <button key={id} onClick={() => setTab(id)}
                        className={`px-1 py-2.5 text-sm font-medium border-b-2 -mb-px ${
                            tab === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                        {lbl}
                    </button>
                ))}
            </div>

            {error && <div className="mb-4 px-3 py-2 rounded border border-red-200 bg-red-50 text-sm text-red-700">{error}</div>}
            {flash && <div className="mb-4 px-3 py-2 rounded border border-green-200 bg-green-50 text-sm text-green-700">{flash}</div>}

            {tab === 'register' && (
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <DataList rows={checks} rowKey="check_id" csvName="check-register" defaultSort={{ key: 'check_date', dir: 'desc' }}
                        empty="No checks recorded yet." rowClass={(c) => (c.status === 'void' ? 'opacity-60' : '')}
                        filters={[{ key: 'status', label: 'Status' }]}
                        columns={[
                            { key: 'check_number', label: 'No.', type: 'number', render: (c) => <span className="font-semibold tabular-nums">{c.check_number}</span> },
                            { key: 'check_date', label: 'Date', type: 'date', render: (c) => fmtDate(c.check_date) },
                            { key: 'payee', label: 'Payee', value: (c) => [c.payee, c.employee_name ? `net wages · ${c.employee_name}` : ''].filter(Boolean).join(' · '),
                                render: (c) => <span className="font-medium">{c.payee}{c.employee_name && <div className="text-xs text-slate-400">net wages · {c.employee_name}</div>}</span> },
                            { key: 'amount', label: 'Amount', type: 'number', render: (c) => <span className="tabular-nums">{money(c.amount)}</span> },
                            { key: 'memo', label: 'Memo', render: (c) => <span className="text-slate-500">{c.memo}</span> },
                            { key: 'status', label: 'Status', render: (c) => <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_TONE[c.status]}`}>{c.status}</span> },
                            { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (c) => (
                                <span className="whitespace-nowrap">
                                    {c.status === 'outstanding' && (
                                        <>
                                            <button onClick={() => clearCheck(c)} className="text-green-700 hover:underline mr-2">Cleared</button>
                                            <button onClick={() => setForm({ ...c })} className="text-blue-600 hover:underline mr-2">Edit</button>
                                            <button onClick={() => voidCheck(c)} className="text-amber-700 hover:underline mr-2">Void</button>
                                        </>
                                    )}
                                    <button onClick={() => removeCheck(c)} className="text-red-600 hover:underline">Remove</button>
                                </span>) },
                        ]} />
                </div>
            )}

            {tab === 'gaps' && (
                <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                        Numbers missing from the book. On a hand-written register a gap usually means a check was torn out and never recorded — worth chasing before it turns up on a statement.
                    </p>
                    {gaps.map(g => (
                        <div key={g.bank_account_id} className="bg-white border border-slate-200 rounded-lg p-4">
                            <div className="flex justify-between items-baseline flex-wrap gap-2">
                                <h3 className="font-semibold text-slate-800">{g.label}</h3>
                                <span className="text-xs text-slate-500 tabular-nums">
                                    {g.recorded} recorded · {g.lowest ?? '—'} to {g.highest ?? '—'}
                                </span>
                            </div>
                            {g.missing_total > 0 ? (
                                <div className="mt-2">
                                    <div className="text-sm font-medium text-amber-800 mb-1">{g.missing_total} number(s) unaccounted for</div>
                                    <ul className="text-sm text-slate-700 space-y-0.5">
                                        {g.gaps.map((x, i) => (
                                            <li key={i} className="tabular-nums">
                                                {x.from === x.to ? `Check ${x.from}` : `Checks ${x.from}–${x.to}`}
                                                <span className="text-slate-400"> ({x.count})</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <p className="text-sm text-green-700 mt-1">No gaps — every number in the range is accounted for.</p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {tab === 'outstanding' && outstanding && (
                <div className="bg-white border border-slate-200 rounded-lg">
                    <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-baseline">
                        <h3 className="font-semibold text-slate-800">Outstanding checks</h3>
                        <span className="text-sm text-slate-600 tabular-nums">{outstanding.count} totalling {money(outstanding.total)}</span>
                    </div>
                    <p className="px-4 py-2 text-xs text-slate-500 border-b border-slate-100">
                        These are the reconciling items between the ledger's bank balance and a statement.
                    </p>
                    <div className="p-3">
                        <DataList rows={outstanding.checks} rowKey="check_id" csvName="outstanding-checks" defaultSort={{ key: 'check_date', dir: 'asc' }} empty="Nothing outstanding."
                            columns={[
                                { key: 'check_number', label: 'No.', type: 'number' },
                                { key: 'check_date', label: 'Date', type: 'date', render: (c) => fmtDate(c.check_date) },
                                { key: 'payee', label: 'Payee' },
                                { key: 'amount', label: 'Amount', type: 'number', render: (c) => <span className="tabular-nums">{money(c.amount)}</span> },
                            ]} />
                    </div>
                </div>
            )}

            {tab === 'books' && (
                <div className="space-y-3">
                    {user?.isManagingBroker && (
                        <div className="flex justify-end">
                            <button className={ghost} onClick={() => setBookForm({ label: '', bank_name: '', account_last4: '', next_check_number: 1001 })}>
                                + Add a check book
                            </button>
                        </div>
                    )}
                    <div className="bg-white border border-slate-200 rounded-lg p-3">
                        <DataList rows={accounts} rowKey="bank_account_id" csvName="check-books" defaultSort={{ key: 'label', dir: 'asc' }} empty="No check books yet."
                            columns={[
                                { key: 'label', label: 'Book', render: (a) => <span className="font-medium">{a.label}</span> },
                                { key: 'bank_name', label: 'Bank', value: (a) => a.bank_name || '—' },
                                { key: 'account_last4', label: 'Account', value: (a) => (a.account_last4 ? `•••• ${a.account_last4}` : '—') },
                                { key: 'ledger', label: 'Ledger', value: (a) => `${a.ledger_code || ''} ${a.ledger_name || ''}`.trim() },
                                { key: 'checks_written', label: 'Checks', type: 'number' },
                                { key: 'highest_number', label: 'Highest', type: 'number', value: (a) => a.highest_number ?? '—' },
                                { key: 'next_check_number', label: 'Next', type: 'number' },
                                { key: 'edit', label: '', sortable: false, csv: false, search: false, render: (a) => (user?.isManagingBroker && (
                                    <button onClick={() => setBookForm({ ...a })} className="text-blue-600 hover:underline">Edit</button>)) },
                            ]} />
                    </div>
                </div>
            )}

            {/* ---------------- RECORD / EDIT A CHECK ---------------- */}
            <Modal open={!!form} onClose={() => setForm(null)} size="md"
                title={form?.check_id ? `Edit check ${form.check_number}` : 'Record a hand-written check'}
                footer={<>
                    <button className={ghost} onClick={() => setForm(null)}>Cancel</button>
                    <button className={btn} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
                </>}>
                {form && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                            <div><label className={label}>Check book</label>
                                <select className={input} value={form.bank_account_id}
                                    onChange={e => {
                                        const b = accounts.find(a => String(a.bank_account_id) === e.target.value);
                                        setForm({ ...form, bank_account_id: e.target.value, check_number: form.check_id ? form.check_number : (b?.next_check_number || '') });
                                    }}>
                                    {accounts.map(a => <option key={a.bank_account_id} value={a.bank_account_id}>{a.label}</option>)}
                                </select></div>
                            <div><label className={label}>Check number</label>
                                <input type="number" className={input} value={form.check_number}
                                    onChange={e => setForm({ ...form, check_number: e.target.value })} /></div>
                            <div><label className={label}>Date</label>
                                <input type="date" className={input} value={iso(form.check_date)}
                                    onChange={e => setForm({ ...form, check_date: e.target.value })} /></div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2"><label className={label}>Pay to the order of</label>
                                <input className={input} value={form.payee}
                                    onChange={e => setForm({ ...form, payee: e.target.value })} /></div>
                            <div><label className={label}>Amount</label>
                                <input type="number" step="0.01" className={input} value={form.amount}
                                    onChange={e => setForm({ ...form, amount: e.target.value })} /></div>
                        </div>
                        <div><label className={label}>Memo</label>
                            <input className={input} value={form.memo || ''}
                                onChange={e => setForm({ ...form, memo: e.target.value })} /></div>

                        {!form.check_id && (
                            <div className="border-t border-slate-200 pt-3">
                                <label className={label}>What is this check for?</label>
                                <select className={input} value={form.account_code || ''}
                                    onChange={e => setForm({ ...form, account_code: e.target.value })}>
                                    <option value="">— it pays something already in the ledger (e.g. payroll net pay) —</option>
                                    {COMMON_ACCOUNTS.map(([code, name]) => <option key={code} value={code}>{code} · {name}</option>)}
                                </select>
                                <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                                    {form.account_code
                                        ? 'This check will post its own ledger entry: debit the account above, credit the bank. Paying a payroll liability this way clears it.'
                                        : 'Leave this blank when the ledger already recorded the payment — posting payroll already credited the bank, so a second entry would count the money twice. The check is recorded against the register only.'}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* ---------------- CHECK BOOK ---------------- */}
            <Modal open={!!bookForm} onClose={() => setBookForm(null)} size="sm"
                title={bookForm?.bank_account_id ? 'Edit check book' : 'Add a check book'}
                footer={<>
                    <button className={ghost} onClick={() => setBookForm(null)}>Cancel</button>
                    <button className={btn} disabled={busy} onClick={saveBook}>{busy ? 'Saving…' : 'Save'}</button>
                </>}>
                {bookForm && (
                    <div className="space-y-3">
                        <div><label className={label}>Label</label>
                            <input className={input} value={bookForm.label}
                                onChange={e => setBookForm({ ...bookForm, label: e.target.value })} /></div>
                        <div><label className={label}>Bank name</label>
                            <input className={input} value={bookForm.bank_name || ''}
                                onChange={e => setBookForm({ ...bookForm, bank_name: e.target.value })} /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className={label}>Account last 4</label>
                                <input maxLength={4} className={input} value={bookForm.account_last4 || ''}
                                    onChange={e => setBookForm({ ...bookForm, account_last4: e.target.value })} /></div>
                            <div><label className={label}>Next check number</label>
                                <input type="number" className={input} value={bookForm.next_check_number}
                                    onChange={e => setBookForm({ ...bookForm, next_check_number: e.target.value })} /></div>
                        </div>
                        <p className="text-xs text-slate-500">
                            Set the next number to match the next blank check in the physical book. No routing or full account number is stored.
                        </p>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ChecksPage;
